import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supportAPI } from '@/services/api';
import { LifeBuoy, Send, Search, Inbox, Loader2, ArrowLeft, X, Settings as SettingsIcon, CreditCard } from 'lucide-react';
import Sidebar from '@/components/shared/Sidebar';
import ChangePasswordCard from '@/components/shared/ChangePasswordCard';
import Pagination, { usePagination } from '@/components/shared/Pagination';
import SupportPayments from './SupportPayments';

const navItems = [
  { to: '/support', icon: Inbox, label: 'Inbox' },
  { to: '/support/outbound', icon: Send, label: 'New outbound' },
  { to: '/support/payments', icon: CreditCard, label: 'Payments' },
  { to: '/support/settings', icon: SettingsIcon, label: 'Settings' },
];

export default function SupportDashboard() {
  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0a0a0b]" data-testid="support-dashboard">
      <Sidebar items={navItems} title="Support" />
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Router />
        </div>
      </main>
    </div>
  );
}

function Router() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const path = window.location.pathname;
  if (ticketId) return <AgentTicketDetail ticketId={ticketId} onBack={() => navigate('/support')} />;
  if (path.endsWith('/outbound')) return <OutboundTicket onDone={() => navigate('/support')} />;
  if (path.endsWith('/payments')) return <SupportPayments />;
  if (path.endsWith('/settings')) return <SupportSettings />;
  return <InboxList onOpen={(id) => navigate(`/support/ticket/${id}`)} />;
}

function SupportSettings() {
  return (
    <div data-testid="support-settings">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-1"><SettingsIcon className="w-5 h-5" /> Settings</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Manage your support agent account</p>
      <div className="space-y-6 max-w-lg">
        <ChangePasswordCard testIdPrefix="support-pw" />
      </div>
    </div>
  );
}

function InboxList({ onOpen }) {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('open');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const p = usePagination(tickets, 20);

  const load = async () => {
    setLoading(true);
    try { const d = await supportAPI.agentInbox({ status, search, limit: 500 }); setTickets(d.tickets || []); setTotal(d.total || 0); }
    catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [status]);

  return (
    <div data-testid="support-inbox">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Support Inbox</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-4">{total} ticket{total === 1 ? '' : 's'} in current view</p>

      <div className="flex gap-2 mb-4 flex-wrap">
        {['open', 'closed', 'all'].map(s => (
          <button key={s} onClick={() => setStatus(s === 'all' ? '' : s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${status === s || (s === 'all' && !status) ? 'bg-slate-900 dark:bg-white text-white dark:text-black' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'}`} data-testid={`filter-${s}`}>
            {s}
          </button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Search subject or user email…"
            className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="agent-search-input" />
        </div>
      </div>

      <div className="space-y-2">
        {loading && <p className="text-slate-500 dark:text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</p>}
        {!loading && tickets.length === 0 && <p className="text-slate-500 dark:text-zinc-500 text-sm bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-6 text-center">No tickets in this view.</p>}
        {p.pageItems.map(t => (
          <button key={t.id} onClick={() => onOpen(t.id)} className="w-full text-left bg-white dark:bg-[#111114] hover:bg-slate-50 dark:hover:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 transition-colors" data-testid={`agent-ticket-${t.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-slate-900 dark:text-white text-sm font-medium truncate">{t.subject}</p>
                <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">{t.user_email} · {t.user_role} · #{t.id.slice(-6)} · {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : ''}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${t.status === 'open' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-slate-200 dark:bg-zinc-700/40 text-slate-600 dark:text-zinc-300'}`}>{t.status}</span>
            </div>
          </button>
        ))}
      </div>
      <Pagination {...p.paginationProps} testIdPrefix="support-inbox-pg" />
    </div>
  );
}

function AgentTicketDetail({ ticketId, onBack }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => { try { setData(await supportAPI.getTicket(ticketId)); } catch { onBack(); } };
  useEffect(() => { load(); }, [ticketId]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try { await supportAPI.postMessage(ticketId, reply); setReply(''); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
    setSending(false);
  };
  const close = async () => { if (!window.confirm('Close this ticket?')) return; await supportAPI.closeTicket(ticketId); await load(); };

  if (!data) return <p className="text-slate-500 dark:text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</p>;
  const t = data.ticket;
  const isOpen = t.status === 'open';

  return (
    <div data-testid="agent-ticket-detail">
      <button onClick={onBack} className="text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 text-sm inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Back to inbox</button>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{t.subject}</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1">{t.user_email} ({t.user_role}) · #{t.id.slice(-6)} · {new Date(t.created_at).toLocaleString()}</p>
        </div>
        {isOpen && <button onClick={close} className="text-xs text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1" data-testid="agent-close-btn"><X className="w-3 h-3" /> Close ticket</button>}
      </div>

      <div className="space-y-3 mb-5">
        {data.messages.map(m => (
          <div key={m.id} className={`rounded-lg p-3 max-w-[85%] ${m.sender === 'support' ? 'bg-emerald-500/5 border border-emerald-500/20 ml-auto' : 'bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50'}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`text-xs font-medium ${m.sender === 'support' ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-zinc-300'}`}>{m.sender_display_name}</span>
              <span className="text-slate-500 dark:text-zinc-600 text-[10px]">{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p className="text-slate-600 dark:text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
          </div>
        ))}
      </div>

      {isOpen && (
        <form onSubmit={send} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-4">
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={4000}
            placeholder="Reply as Numuv Support…" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="agent-reply-input" />
          <div className="flex justify-end mt-2">
            <button type="submit" disabled={sending || !reply.trim()} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2" data-testid="agent-reply-submit-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Reply
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function OutboundTicket({ onDone }) {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await supportAPI.createOutboundTicket({ target_email: email, subject, body });
      onDone();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  return (
    <div data-testid="outbound-form">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Send className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> Send a message</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Initiate a ticket with any user — e.g. a negative-balance notice, important policy update, etc.</p>
      <form onSubmit={submit} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5 space-y-3 max-w-lg">
        <div>
          <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1">Recipient email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} required type="email"
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="outbound-email-input" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1">Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} required maxLength={200}
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="outbound-subject-input" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1">Message</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} required rows={5} maxLength={4000}
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="outbound-body-input" />
        </div>
        <button type="submit" disabled={submitting} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full text-sm font-medium disabled:opacity-50" data-testid="outbound-submit-btn">
          {submitting ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
}
